import DB from '../../../private/db';
import type { IDB } from '../../../private/db/interface';
import type { IPatchStorage, IPatchStorageOptions } from './interface';

const TABLE_NAME = 'IPatchStorage';
const TABLE_MAPPINGS = '++name, hash, data';

function _isPatchStorage(object: unknown): object is IPatchStorage {
    const isNullOrUndefined = (value: unknown): boolean => value === null || value === undefined;

    if (isNullOrUndefined(object) || typeof object !== 'object') {
        return false;
    }

    const record: Partial<IPatchStorage> = object as Record<string, unknown>;

    return (
        !isNullOrUndefined(record.name) &&
        !isNullOrUndefined(record.hash) &&
        !isNullOrUndefined(record.data)
    );
}

function _db(options: IPatchStorageOptions = {}): Promise<IDB> {
    const db = DB().open(options.name ?? TABLE_NAME, {
        version: 1,
        mappings: {
            [TABLE_NAME]: TABLE_MAPPINGS,
        },
    });

    return db;
}

async function load(name: string, options: IPatchStorageOptions = {}): Promise<IPatchStorage> {
    const db = await _db(options);

    await db.open();

    const table = db.table(TABLE_NAME);

    const result = await table.get(name).catch(() => undefined);

    db.close();

    if (!result) {
        throw new Error(`Unable to find patch-storage named: ${name}`);
    } else if (!_isPatchStorage(result)) {
        throw new Error(
            `Object returned is not patch-storage type: ${name}, ${JSON.stringify(result)}`,
        );
    }

    return result;
}

async function save(data: IPatchStorage, options: IPatchStorageOptions = {}) {
    const db = await _db(options);

    await db.open();

    const table = db.table(TABLE_NAME);

    const result = await table.addOrUpdate(data.name, data);

    db.close();

    if (!result) {
        throw new Error('Failed to save data into patch-storage table');
    }
}

async function remove(name: string, options: IPatchStorageOptions = {}) {
    const db = await _db(options);

    await db.open();

    const table = db.table(TABLE_NAME);

    await table.delete(name);

    db.close();
}

export default Object.freeze({
    load,
    save,
    remove,
});
